import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { getFontFamily, useThemePalette } from '@/config/theme';
import { useLocale } from '@/lib/i18n/LocaleProvider';

// Minimal structural shape of the React Navigation tab-bar props we use, so we
// don't depend on @react-navigation/bottom-tabs types (not a direct dependency).
export interface FloatingTabBarProps {
  state: { index: number; routes: { key: string; name: string }[] };
  descriptors: Record<string, { options: { title?: string } }>;
  navigation: {
    emit: (event: { type: 'tabPress'; target: string; canPreventDefault: true }) => {
      defaultPrevented: boolean;
    };
    navigate: (name: string) => void;
  };
}

type IoniconName = keyof typeof Ionicons.glyphMap;

/** Per-route Ionicons: filled when active, outline when not (FotMob-style). */
const ICONS: Record<string, { on: IoniconName; off: IoniconName }> = {
  index: { on: 'football', off: 'football-outline' },
  fantasy: { on: 'shirt', off: 'shirt-outline' },
  games: { on: 'game-controller', off: 'game-controller-outline' },
  leaderboard: { on: 'podium', off: 'podium-outline' },
  leagues: { on: 'trophy', off: 'trophy-outline' },
  profile: { on: 'person', off: 'person-outline' },
};

/** Routes that exist for navigation but are NOT shown as top-level tabs.
 *  Predictions is reached internally (More menu + the per-match poll). */
const HIDDEN_TABS = new Set(['predictions']);

/**
 * FotMob-style floating pill tab bar: a blurred-dark rounded bar over the canvas
 * with clean Ionicons that fill on the active tab, which also gets a soft glass
 * highlight. Rendered in normal flow so the navigator insets content above it.
 */
export function FloatingTabBar({ state, descriptors, navigation }: FloatingTabBarProps) {
  const palette = useThemePalette();
  const insets = useSafeAreaInsets();
  const { locale } = useLocale();

  return (
    <View
      style={{
        paddingHorizontal: 14,
        paddingTop: 8,
        paddingBottom: insets.bottom > 0 ? insets.bottom + 4 : 16,
        backgroundColor: palette.deep,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: 'rgba(20,20,42,0.96)',
          borderWidth: 1,
          borderColor: palette.glassBorder,
          borderRadius: 24,
          paddingVertical: 7,
          paddingHorizontal: 6,
          shadowColor: '#000',
          shadowOpacity: 0.5,
          shadowRadius: 16,
          shadowOffset: { width: 0, height: 10 },
          elevation: 12,
        }}
      >
        {state.routes.map((route, index) => {
          if (HIDDEN_TABS.has(route.name)) return null;
          const focused = state.index === index;
          const { options } = descriptors[route.key];
          const label = typeof options.title === 'string' ? options.title : route.name;
          const color = focused ? palette.voltBright : palette.inactive;
          const icon = ICONS[route.name] ?? ICONS.index;

          const onPress = () => {
            const event = navigation.emit({
              type: 'tabPress',
              target: route.key,
              canPreventDefault: true,
            });
            if (!focused && !event.defaultPrevented) {
              navigation.navigate(route.name);
            }
          };

          return (
            <Pressable
              key={route.key}
              accessibilityRole="button"
              accessibilityState={{ selected: focused }}
              accessibilityLabel={label}
              onPress={onPress}
              style={{ flex: 1, alignItems: 'center', gap: 3, paddingVertical: 3 }}
            >
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 5,
                  borderRadius: 14,
                  backgroundColor: focused ? palette.voltGlass : 'transparent',
                }}
              >
                <Ionicons name={focused ? icon.on : icon.off} size={21} color={color} />
              </View>
              <Text
                style={{
                  fontFamily: getFontFamily(locale, focused ? 'bold' : 'medium'),
                  fontSize: 9.5,
                  color,
                }}
              >
                {label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}
